import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import {
  isBookCollectionKey,
  isKbMembershipKey,
  knowledgeBaseLabel,
  stripKbMembershipKeys,
  withKbMembership,
} from "@/lib/knowledgeBaseRegistry";
import {
  articlePath,
  type KnowledgeArticle,
  type KnowledgeArticleStatus,
} from "@/lib/knowledgeModel";
import { knowledgeArticlesPath } from "@/lib/knowledgePaths";
import {
  KnowledgeChrome,
  KnowledgeError,
  KnowledgeNeedsBase,
  knowledgeSecondaryLinkClass,
} from "@/pages/knowledge/KnowledgeChrome";
import { UploadMarkdownButton } from "@/pages/knowledge/UploadMarkdownButton";
import { useCustomBooks } from "@/pages/knowledge/useCustomBooks";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";
import { useKnowledgeRouteBase } from "@/pages/knowledge/useKnowledgeRouteBase";

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-slate-900";

export function KnowledgeArticlePage({ mode }: { mode: "create" | "edit" }) {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { books: customBooks } = useCustomBooks();
  const { activeId, active, hasBases } = useKnowledgeBases();
  const baseId = useKnowledgeRouteBase() || activeId;
  const books = useMemo(
    () => customBooks.map((book) => ({ collectionKey: book.collectionKey, title: book.title })),
    [customBooks],
  );
  const knownBookKeys = useMemo(() => new Set(books.map((book) => book.collectionKey)), [books]);

  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [status, setStatus] = useState<KnowledgeArticleStatus>("draft");
  const [collectionKeys, setCollectionKeys] = useState<string[]>([]);
  const [storedKbKeys, setStoredKbKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") return;
    if (!articleId) {
      setError("Article id is missing.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      const res = await apiGet<KnowledgeArticle & { error?: string }>(
        `/api/knowledge/articles/${encodeURIComponent(articleId)}`,
      );
      if (cancelled) return;
      if (!res.ok || !res.data?.id) {
        setError(res.error || "Could not load article.");
        setLoading(false);
        return;
      }
      setTitle(res.data.title || "");
      setMarkdown(typeof res.data.markdown === "string" ? res.data.markdown : "");
      setStatus(res.data.status === "published" ? "published" : "draft");
      {
        const keys = Array.isArray(res.data.collectionKeys) ? res.data.collectionKeys : [];
        setStoredKbKeys(keys.filter(isKbMembershipKey));
        setCollectionKeys(keys.filter(isBookCollectionKey));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, articleId]);

  function toggleKey(key: string) {
    if (isKbMembershipKey(key)) return;
    setCollectionKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key].sort(),
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    if (mode === "create" && !activeId) {
      setError("Select or create a knowledge base first.");
      return;
    }
    setSaving(true);
    setError(null);
    const nextKeys =
      mode === "create"
        ? withKbMembership(collectionKeys, activeId)
        : Array.from(new Set([...stripKbMembershipKeys(collectionKeys), ...storedKbKeys])).sort();
    const body = {
      title: title.trim(),
      markdown,
      status,
      collectionKeys: nextKeys,
    };
    const res =
      mode === "create"
        ? await apiPost<KnowledgeArticle>("/api/knowledge/articles", body)
        : await apiPatch<KnowledgeArticle>(
            `/api/knowledge/articles/${encodeURIComponent(articleId || "")}`,
            body,
          );
    setSaving(false);
    if (!res.ok || !res.data?.id) {
      setError(res.error || "Could not save article.");
      return;
    }
    void navigate(articlePath(baseId, res.data.id), { replace: mode === "create" });
  }

  const extraKeys = collectionKeys.filter((key) => !knownBookKeys.has(key) && isBookCollectionKey(key));
  const viewHref = articleId ? articlePath(baseId, articleId) : knowledgeArticlesPath(baseId);

  if (mode === "create" && !hasBases) {
    return (
      <KnowledgeChrome
        title="New article"
        description="Articles belong to a knowledge base. Create one first."
        badge="Markdown"
        showBaseSwitcher={false}
      >
        <KnowledgeNeedsBase />
      </KnowledgeChrome>
    );
  }

  return (
    <KnowledgeChrome
      title={mode === "create" ? "New article" : "Edit article"}
      description={`Write markdown for "${active ? knowledgeBaseLabel(active) : "this knowledge base"}". Published articles are what One AI reads. Books are optional collection keys on this same article.`}
      badge="Markdown"
      actions={
        <>
          <Link to={knowledgeArticlesPath(baseId)} className={knowledgeSecondaryLinkClass}>
            Articles
          </Link>
          {mode === "create" ? <UploadMarkdownButton onError={setError} /> : null}
          {mode === "edit" ? (
            <Link to={viewHref} className={knowledgeSecondaryLinkClass}>
              View
            </Link>
          ) : null}
        </>
      }
    >
      {error ? <KnowledgeError message={error} /> : null}
      {loading ? <p className="text-sm text-slate-500">Loading article…</p> : null}
      {!loading ? (
        <form
          onSubmit={(event) => void onSubmit(event)}
          className="max-w-3xl space-y-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40"
        >
          <label className="block text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">Title</span>
            <input
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Article title"
              className={fieldClass}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value === "published" ? "published" : "draft")}
              className={fieldClass}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              One AI reads published articles. Drafts stay in the knowledge base for editing.
            </span>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">Markdown</span>
            <textarea
              value={markdown}
              onChange={(event) => setMarkdown(event.target.value)}
              rows={18}
              placeholder={"# Heading\n\nWrite the article in markdown."}
              className={`${fieldClass} font-mono`}
            />
          </label>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Books <span className="font-normal text-slate-500">(optional)</span>
            </legend>
            <p className="text-xs text-slate-500">
              Leave all off for a standalone article. Turn on books only when you want this article in
              those collections. Saving writes the selected keys as collectionKeys.
            </p>
            {books.length === 0 ? (
              <p className="text-sm text-slate-500">
                No books are available yet. Create a custom book from Books, or save a standalone
                article.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {books.map((book) => {
                  const active = collectionKeys.includes(book.collectionKey);
                  return (
                    <button
                      key={book.collectionKey}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleKey(book.collectionKey)}
                      className={
                        active
                          ? "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white"
                          : "rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                      }
                    >
                      {book.title}
                    </button>
                  );
                })}
              </div>
            )}
            {extraKeys.length > 0 ? (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500">
                  Other collection keys already stored on this article. Leave them on to keep the same membership.
                </p>
                <div className="flex flex-wrap gap-2">
                  {extraKeys.map((key) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed
                      onClick={() => toggleKey(key)}
                      className="rounded-md bg-slate-700 px-3 py-1.5 font-mono text-xs font-medium text-white"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </fieldset>
          <button
            type="submit"
            disabled={saving || !title.trim()}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : mode === "create" ? "Create article" : "Save changes"}
          </button>
        </form>
      ) : null}
    </KnowledgeChrome>
  );
}
